import { useState, useEffect } from 'react';
import { Tooltip, Modal, Button, Space, Tag, Badge, Typography, message, Spin } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { EntityImage } from './EntityImage';

const { Text } = Typography;

// Module-level cache so all instances share fetched entity data
const entityCache = {};

// Drop one entity from the chip cache — call after an edit that changes what
// chips show (name, picture) so they refetch instead of staying stale.
export function invalidateEntityAvatar(entityId) {
    delete entityCache[entityId];
}

// Duplicated in Home/Homepage.jsx — keep both in sync
// (this is the canonical/most complete copy of the type→color mapping).
const ENTITY_TYPE_COLORS = {
    context:      '#52c41a',
    relationship: '#faad14',
    person:       '#1677ff',
    app:          '#722ed1',
};

function useEntity(entityId) {
    const api = useApi();
    const [entity, setEntity] = useState(entityCache[entityId] || null);

    useEffect(() => {
        if (entityCache[entityId]) {
            setEntity(entityCache[entityId]);
            return;
        }
        api.get(`/api/entities/${entityId}`)
            .catch(() => null)
            .then(data => {
                if (data) {
                    entityCache[entityId] = data;
                    setEntity(data);
                }
            });
    }, [entityId]);

    return entity;
}

// First letter of the first two words, capitalised — the chip label here and
// the entity page's stand-in profile picture read the same name the same way.
export function initials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
}

function useConnections(entityId, open) {
    const api = useApi();
    const { user } = useAuth();
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !entityId || !user?.id) return;
        setLoading(true);
        api.get('/api/connections', { params: { entity_id: entityId, user_id: user.id } })
            .catch(() => [])
            .then(data => setConnections(data))
            .finally(() => setLoading(false));
    }, [entityId, open, user?.id]);

    return { connections, loading };
}


function EntityDetailModal({ entityId, entity, color, open, onClose }) {
    const { connections, loading: connectionsLoading } = useConnections(entityId, open);

    const copyId = () => {
        navigator.clipboard.writeText(`ID:${entityId}`).then(() => {
            message.success(`Copied ID:${entityId}`);
            onClose();
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={400}
            title={
                <Space>
                    <Tag style={{ fontWeight: 700, fontSize: 12, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Badge color={color} style={{ marginInlineEnd: 0 }} />
                        {initials(entity?.name) || entityId}
                    </Tag>
                    <span>{entity?.name || `Entity ${entityId}`}</span>
                </Space>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div>
                    <Tag color={color}>{entity?.entity_type || '—'}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>ID:{entityId}</Text>
                </div>
                {entity?.description && (
                    <Text style={{ fontSize: 13 }}>
                        {(() => {
                            const sentences = entity.description.match(/[^.!?]+[.!?]+/g) || [];
                            if (sentences.length <= 2) return entity.description;
                            return sentences.slice(0, 2).join('').trim() + '…';
                        })()}
                    </Text>
                )}

                <Space style={{ marginTop: 8 }} wrap>
                    <Button icon={<CopyOutlined />} size="small" onClick={copyId}>
                        Copy ID:{entityId}
                    </Button>
                    <Button
                        icon={<LinkOutlined />}
                        size="small"
                        type="primary"
                        onClick={() => window.open(`/entity/${entityId}`, '_blank')}
                    >
                        Open entity
                    </Button>
                </Space>

                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Connections {!connectionsLoading && `(${connections.length})`}
                    </Text>
                    <div style={{ marginTop: 6 }}>
                        {connectionsLoading ? (
                            <Spin size="small" />
                        ) : connections.length === 0 ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>No connections</Text>
                        ) : (
                            <EntityAvatarGroup
                                entityIds={connections.map(conn =>
                                    conn.entity_a_id === entityId ? conn.entity_b_id : conn.entity_a_id
                                )}
                                size={20}
                            />
                        )}
                    </div>
                </div>
            </Space>
        </Modal>
    );
}

// The picture alone (no chip, no initials) for callers that only hold an id
// (SenderAvatar). Shares the chip's cache, so a picture and a chip for the
// same entity cost one fetch.
export function EntityImageById({ entityId, size = 24, onClick, style }) {
    const entity = useEntity(entityId);
    return (
        <span
            onClick={onClick}
            style={{ display: 'inline-flex', flexShrink: 0, cursor: onClick ? 'pointer' : undefined, ...style }}
        >
            <EntityImage entity={entity || { id: entityId }} size={size} />
        </span>
    );
}

// `onClick` opts out of the detail modal entirely — pass it where the chip is
// just a shortcut to the entity (e.g. the Messages channel list) rather than a
// place to inspect connections.
export function EntityAvatar({ entityId, size = 32, showBadge = true, onClick }) {
    const entity = useEntity(entityId);
    const [modalOpen, setModalOpen] = useState(false);
    const color = ENTITY_TYPE_COLORS[entity?.entity_type] || '#8c8c8c';
    const isSmall = size <= 20;

    return (
        <>
            <Tooltip title={entity ? `${entity.entity_type} · ${entity.name}` : `Entity ${entityId}`}>
                <Tag
                    onClick={onClick || (() => setModalOpen(true))}
                    style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontWeight: 700,
                        fontSize: isSmall ? 10 : 12,
                        padding: isSmall ? '1px 5px' : '2px 8px',
                        borderRadius: 4,
                        margin: 0,
                        letterSpacing: '0.04em',
                        verticalAlign: 'middle',
                        flexShrink: 0,
                        lineHeight: isSmall ? '16px' : '20px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    <EntityImage entity={entity} size={isSmall ? 14 : 18} />
                    {showBadge && <Badge color={color} style={{ marginInlineEnd: 0 }} />}
                    {initials(entity?.name) || entityId}
                </Tag>
            </Tooltip>
            {!onClick && (
                <EntityDetailModal
                    entityId={entityId}
                    entity={entity}
                    color={color}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    );
}

function useEntitiesGrouped(entityIds) {
    const api = useApi();
    const [grouped, setGrouped] = useState({});

    useEffect(() => {
        let alive = true;
        const results = {};

        const fetchOne = async (id) => {
            if (entityCache[id]) return entityCache[id];
            const data = await api.get(`/api/entities/${id}`).catch(() => null);
            if (data) entityCache[id] = data;
            return data;
        };

        Promise.all(entityIds.map(fetchOne)).then(entities => {
            if (!alive) return;
            entities.forEach(e => {
                if (!e) return;
                const type = e.entity_type || 'unknown';
                if (!results[type]) results[type] = [];
                results[type].push(e);
            });
            setGrouped(results);
        });

        return () => { alive = false; };
    }, [entityIds.join(',')]);

    return grouped;
}

const TYPE_LABELS = {
    context:      'Contexts',
    relationship: 'Relationships',
    person:       'People',
    app:          'Apps',
    unknown:      'Other',
};

export function EntityAvatarGroup({ entityIds, size = 32 }) {
    const grouped = useEntitiesGrouped(entityIds);
    const types = Object.keys(grouped);

    return (
        <div style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '6px 8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
        }}>
            {types.map(type => (
                <span key={type} style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: ENTITY_TYPE_COLORS[type] || '#8c8c8c',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                    }}>
                        {TYPE_LABELS[type] || type}
                    </span>
                    <span style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap' }}>
                        {grouped[type].map(e => (
                            <EntityAvatar key={e.id} entityId={e.id} size={size} />
                        ))}
                    </span>
                </span>
            ))}
        </div>
    );
}

/**
 * Parses a message string and replaces ID:N or ID:N, ID:M, ... patterns
 * with EntityAvatar / EntityAvatarGroup components.
 */
export function renderWithEntityAvatars(text) {
    if (!text) return text;

    // Normalise "ID:1, and ID:2" → "ID:1, ID:2" before parsing
    const normalised = text.replace(/,?\s+and\s+(?=ID:\d+)/gi, ', ');

    // Match one or more consecutive "ID:N" tokens (with optional commas/spaces
    // between). Not /g: split() ignores the flag, and a global regex would carry
    // lastIndex between calls.
    const groupPattern = /((?:ID:\d+(?:[,\s]+)?)+)/;
    const parts = normalised.split(groupPattern);

    return parts.map((part, i) => {
        if (!/ID:\d+/.test(part)) return part;

        // Extract all numeric IDs from this group
        const ids = [...part.matchAll(/ID:(\d+)/g)].map(m => Number(m[1]));

        if (ids.length === 0) return part;
        if (ids.length === 1) return <EntityAvatar key={i} entityId={ids[0]} size={20} />;
        return <EntityAvatarGroup key={i} entityIds={ids} size={20} />;
    });
}
